import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Users, Loader2, Crown, UserPlus, X, 
  Receipt, ChevronRight, UserMinus, Sparkles, Wallet
} from 'lucide-react';
import { groupsAPI } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

// Vibrant gradient for header
const HEADER_GRADIENT = 'from-violet-500 via-purple-500 to-fuchsia-500';

const GroupDetailPage = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [group, setGroup] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMemberPhone, setNewMemberPhone] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState(null);

  useEffect(() => {
    fetchGroupData();
  }, [groupId]);

  const fetchGroupData = async () => {
    try {
      setLoading(true);
      const [groupRes, expensesRes] = await Promise.all([
        groupsAPI.get(groupId),
        groupsAPI.getExpenses(groupId),
      ]);
      setGroup(groupRes.data);
      setExpenses(expensesRes.data);
    } catch (err) {
      setError('Couldn\'t load group');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (newMemberPhone.length !== 10) return;
    
    setAddingMember(true);
    try {
      await groupsAPI.addMember(groupId, newMemberPhone);
      toast.success('Member added!');
      setNewMemberPhone('');
      setShowAddMember(false);
      fetchGroupData();
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to add member';
      toast.error(typeof message === 'string' ? message : 'Failed to add member');
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (memberUserId) => {
    setRemovingMemberId(memberUserId);
    try {
      await groupsAPI.removeMember(groupId, memberUserId);
      toast.success('Member removed');
      fetchGroupData();
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to remove member';
      toast.error(typeof message === 'string' ? message : 'Failed to remove member');
    } finally {
      setRemovingMemberId(null);
    }
  };

  const formatCurrency = (amount) => {
    const num = Math.abs(amount);
    if (num === Math.floor(num)) {
      return `₹${num.toLocaleString('en-IN')}`;
    }
    return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const isAdmin = group?.members?.find(m => m.user_id === user?.id)?.is_admin;

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        <p className="text-lg font-medium mb-4">{error || 'Group not found'}</p>
        <button onClick={() => navigate('/groups')} className="btn-secondary">
          Back to Groups
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-32">
      {/* Vibrant Header */}
      <div className={`bg-gradient-to-br ${HEADER_GRADIENT} text-white relative overflow-hidden`}>
        <Sparkles className="absolute top-4 right-4 w-6 h-6 text-white/30" />
        <Sparkles className="absolute bottom-8 left-8 w-4 h-4 text-white/20" />
        
        <header className="px-6 py-4 flex items-center gap-4">
          <button 
            onClick={() => navigate('/groups')}
            className="hover:opacity-70 transition-opacity"
            data-testid="back-button"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-heading font-bold flex-1 truncate">{group.name}</h1>
        </header>
        
        <div className="px-6 pb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Users className="w-8 h-8 text-white" />
            </div>
            <div>
              <p className="text-white/80 text-sm">
                {group.member_count} member{group.member_count !== 1 ? 's' : ''}
              </p>
              {isAdmin && (
                <span className="inline-flex items-center gap-1 bg-white/20 px-2 py-0.5 rounded-full text-xs font-medium mt-1">
                  <Crown className="w-3 h-3" />
                  You're Admin
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <main className="p-6 space-y-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="card-base bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-300">
            <Wallet className="w-5 h-5 text-emerald-600 mb-2" />
            <p className="text-xs text-emerald-600 uppercase font-medium">Total Expenses</p>
            <p className="text-xl font-heading font-bold text-emerald-700">
              {expenses.length}
            </p>
          </div>
          <div className="card-base bg-gradient-to-br from-violet-50 to-purple-50 border-violet-300">
            <Receipt className="w-5 h-5 text-violet-600 mb-2" />
            <p className="text-xs text-violet-600 uppercase font-medium">Total Amount</p>
            <p className="text-xl font-heading font-bold text-violet-700">
              {formatCurrency(expenses.reduce((sum, e) => sum + e.total_amount, 0))}
            </p>
          </div>
        </div>

        {/* Members Section */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-heading font-bold flex items-center gap-2">
              <Users className="w-5 h-5" />
              Members
            </h2>
            {isAdmin && (
              <button
                onClick={() => setShowAddMember(true)}
                className="flex items-center gap-1 text-sm font-bold text-violet-600 hover:text-violet-700"
                data-testid="add-member-toggle"
              >
                <UserPlus className="w-4 h-4" />
                Add
              </button>
            )}
          </div>

          {/* Add Member Input */}
          {showAddMember && (
            <div className="mb-4 p-4 rounded-xl border-2 border-dashed border-violet-300 bg-violet-50">
              <div className="flex gap-2">
                <div className="flex items-center justify-center w-14 h-12 rounded-lg border-2 border-black bg-white font-bold text-xs">
                  +91
                </div>
                <input
                  type="tel"
                  value={newMemberPhone}
                  onChange={(e) => setNewMemberPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Phone number"
                  className="input-base flex-1 h-12"
                  data-testid="new-member-phone"
                />
                <button
                  onClick={handleAddMember}
                  disabled={newMemberPhone.length !== 10 || addingMember}
                  className="px-4 h-12 rounded-xl bg-violet-600 text-white font-bold disabled:opacity-50"
                  data-testid="confirm-add-member"
                >
                  {addingMember ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
                </button>
                <button
                  onClick={() => {
                    setShowAddMember(false);
                    setNewMemberPhone('');
                  }}
                  className="w-12 h-12 rounded-xl border-2 border-black flex items-center justify-center hover:bg-neutral-100"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {group.members?.map((member, index) => (
              <div 
                key={member.id}
                className="flex items-center gap-3 p-3 rounded-xl border-2 border-black bg-white"
                data-testid={`member-${member.user_id}`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm bg-gradient-to-br ${
                  index === 0 ? 'from-amber-400 to-orange-500' :
                  index === 1 ? 'from-pink-500 to-rose-500' :
                  index === 2 ? 'from-cyan-500 to-blue-500' :
                  'from-emerald-500 to-teal-500'
                }`}>
                  {member.user_name?.[0]?.toUpperCase() || member.user_phone?.[0] || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate flex items-center gap-2">
                    {member.user_name || 'Unknown'}
                    {member.is_admin && (
                      <Crown className="w-4 h-4 text-amber-500" />
                    )}
                    {member.user_id === user?.id && (
                      <span className="text-xs text-neutral-400">(You)</span>
                    )}
                  </p>
                  <p className="text-sm text-neutral-500">
                    +91 {member.user_phone}
                  </p>
                </div>
                {isAdmin && !member.is_admin && member.user_id !== user?.id && (
                  <button
                    onClick={() => handleRemoveMember(member.user_id)}
                    disabled={removingMemberId === member.user_id}
                    className="w-8 h-8 rounded-full hover:bg-red-100 flex items-center justify-center text-red-500 transition-colors"
                    data-testid={`remove-member-${member.user_id}`}
                  >
                    {removingMemberId === member.user_id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <UserMinus className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Expenses Section */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-heading font-bold flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Expenses
            </h2>
          </div>

          {expenses.length === 0 ? (
            <div className="card-base text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center">
                <Receipt className="w-8 h-8 text-white" />
              </div>
              <p className="font-medium mb-1">No expenses yet</p>
              <p className="text-sm text-neutral-500 mb-4">
                Add the first expense to start splitting!
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {expenses.map((expense) => (
                <button
                  key={expense.id}
                  onClick={() => navigate(`/expenses/${expense.id}`)}
                  className="w-full card-base hover:-translate-y-1 transition-transform text-left flex items-center gap-4"
                  data-testid={`expense-${expense.id}`}
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center">
                    <Receipt className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate">
                      {expense.description || 'Expense'}
                    </p>
                    <p className="text-sm text-neutral-500">
                      Paid by {expense.paid_by_name || 'Someone'} • {formatDate(expense.created_at)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg">
                      {formatCurrency(expense.total_amount)}
                    </p>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      expense.status === 'settled' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {expense.status === 'settled' ? 'Settled' : 'Pending'}
                    </span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-neutral-400" />
                </button>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Fixed Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-6 bg-white border-t-2 border-black">
        <button
          onClick={() => navigate(`/groups/${groupId}/expense`)}
          className="btn-primary w-full flex items-center justify-center gap-2"
          data-testid="add-expense-button"
        >
          <Plus className="w-5 h-5" />
          Add Group Expense
        </button>
      </div>
    </div>
  );
};

export default GroupDetailPage;
